
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, FolderIcon, UserIcon, Trash2, FileText, Trophy } from 'lucide-react';
import { Project } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import EstimateStatusBadge from '@/components/estimates/EstimateStatusBadge';
import ProjectStatusBadgeEditable from '@/components/projects/ProjectStatusBadgeEditable';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useEstimates } from '@/hooks/useEstimates';
import { useProjects } from '@/hooks/useProjects';

interface ProjectCardProps {
  project: Project;
}

const ProjectCard = ({ project }: ProjectCardProps) => {
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { estimates } = useEstimates(project.id);
  const { deleteProject, isDeleting } = useProjects();

  const totalAmount = estimates.reduce((sum, estimate) => sum + estimate.totalAmount, 0);

  // "Modificato di recente": project.updatedAt o un preventivo aggiornato/creato
  // nelle ultime 24h. Soglia in ms.
  const recentlyActive = useMemo(() => {
    const DAY_MS = 24 * 60 * 60 * 1000;
    const now = Date.now();
    const ts = (d?: Date | null) => (d?.getTime?.() ?? 0);
    const projectTs = ts(project.updatedAt);
    const estimateTs = Math.max(0, ...estimates.map(e => Math.max(ts(e.updatedAt), ts(e.createdAt))));
    const newest = Math.max(projectTs, estimateTs);
    if (newest === 0) return null;
    const ageMs = now - newest;
    return ageMs >= 0 && ageMs < DAY_MS ? ageMs : null;
  }, [project.updatedAt, estimates]);

  const recentLabel = (ageMs: number): string => {
    const h = Math.floor(ageMs / (60 * 60 * 1000));
    if (h < 1) return 'aggiornato or ora';
    if (h === 1) return 'aggiornato 1h fa';
    return `aggiornato ${h}h fa`;
  };

  // Stats inline per la card
  const projectStats = useMemo(() => {
    const wonEsts = estimates.filter(e => e.status === 'won' || e.status === 'contracted');
    const openEsts = estimates.filter(e => e.status === 'sent' || e.status === 'pending' || e.status === 'draft');
    const wonValue = wonEsts.reduce((s, e) => s + e.totalAmount, 0);
    const openValue = openEsts.reduce((s, e) => s + e.totalAmount, 0);
    // Ultimo preventivo: il più recente per updatedAt (fallback createdAt)
    const latest = estimates.length === 0 ? null : [...estimates].sort((a, b) => {
      const ta = (a.updatedAt ?? a.createdAt)?.getTime?.() ?? 0;
      const tb = (b.updatedAt ?? b.createdAt)?.getTime?.() ?? 0;
      return tb - ta;
    })[0];
    return { wonCount: wonEsts.length, openCount: openEsts.length, wonValue, openValue, latest };
  }, [estimates]);

  const handleDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    deleteProject(project.id);
    setShowDeleteDialog(false);
  };

  return (
    <>
      <div className="relative group">
        <Link to={`/projects/${project.id}`}>
          <Card className="h-full overflow-hidden transition-all duration-200 hover:shadow-md hover:-translate-y-1">
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-start gap-2 min-w-0 flex-1">
                  {recentlyActive !== null && (
                    <span
                      className="relative inline-flex shrink-0 mt-2"
                      title={recentLabel(recentlyActive)}
                    >
                      <span className="absolute inline-flex h-2 w-2 rounded-full bg-emerald-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                    </span>
                  )}
                  <h3 className="font-semibold text-lg line-clamp-2">{project.name}</h3>
                </div>
                <div className="flex items-center gap-2">
                  {/* Wrapper per stoppare la propagazione del click sul Link parent */}
                  <span
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    className="inline-flex"
                  >
                    <ProjectStatusBadgeEditable
                      status={project.status}
                      projectId={project.id}
                    />
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleDelete}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <p className="text-muted-foreground text-sm line-clamp-2 mb-3">
                {project.description || 'Nessuna descrizione disponibile'}
              </p>

              <div className="space-y-1.5 text-sm text-muted-foreground mb-3">
                <div className="flex items-center">
                  <UserIcon className="h-3.5 w-3.5 mr-2" />
                  <span className="truncate">{project.client}</span>
                </div>
                <div className="flex items-center">
                  <CalendarIcon className="h-3.5 w-3.5 mr-2" />
                  <span>Creato {project.createdAt.toLocaleDateString('it-IT')}</span>
                </div>
              </div>

              {/* Ultimo preventivo */}
              {projectStats.latest ? (
                <div className="border-t pt-2.5 mt-1">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground/80 mb-1">Ultimo preventivo</div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="text-sm font-medium truncate">{projectStats.latest.name}</span>
                    </div>
                    <EstimateStatusBadge
                      status={projectStats.latest.status}
                      size="sm"
                      createdAt={projectStats.latest.createdAt}
                      sentAt={projectStats.latest.sentAt}
                      wonAt={projectStats.latest.wonAt}
                      lostAt={projectStats.latest.lostAt}
                    />
                  </div>
                </div>
              ) : (
                <div className="border-t pt-2.5 mt-1 text-xs text-muted-foreground italic">
                  Nessun preventivo ancora
                </div>
              )}
            </CardContent>

            <CardFooter className="bg-muted/40 px-6 py-3 flex flex-col items-stretch gap-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <FolderIcon className="h-3.5 w-3.5" />
                  {estimates.length} preventiv{estimates.length === 1 ? 'o' : 'i'}
                </span>
                <span className="font-medium">€ {Math.round(totalAmount).toLocaleString('it-IT')}</span>
              </div>
              {projectStats.wonCount > 0 && (
                <div className="flex items-center justify-between text-xs text-emerald-700">
                  <span className="flex items-center gap-1">
                    <Trophy className="h-3 w-3" />
                    {projectStats.wonCount} vint{projectStats.wonCount === 1 ? 'o' : 'i'}
                  </span>
                  <span className="font-medium">€ {Math.round(projectStats.wonValue).toLocaleString('it-IT')}</span>
                </div>
              )}
            </CardFooter>
          </Card>
        </Link>
      </div>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina Progetto</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare il progetto "{project.name}"? 
              Questa azione eliminerà anche tutti i preventivi associati e non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
            >
              {isDeleting ? 'Eliminazione...' : 'Elimina'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ProjectCard;
